import { EndpointUrl } from 'api/endpoint';
import { useFetcher } from 'api/fetcher';
import { useCurrentUser } from 'api/providers/authProvider';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';

export interface FieldAutofillHistory {
  id: string;
  entity_id: string;
  status: 'pending' | 'processing' | 'review' | 'failed' | 'completed';
  created_at: string;
  new_fields: Record<string, any>;
  processed_at: string;
  workspace_id: string;
  user_id: string;
}

export function useFieldAutofillHistory() {
  return useFetcher<FieldAutofillHistory[]>(
    `datamaid/history/field-autofill?needs_review=true`,
  );
}

export function useFieldAutofillStatus(entityId: string | null) {
  return useFetcher<FieldAutofillHistory[]>(
    entityId ? `datamaid/field-autofill/${entityId}/status` : null,
    true,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: (data) => {
        if (data?.length) {
          if (data[0].status === 'pending' || data[0].status === 'processing') {
            return 2000;
            // return 999999;
          }
        }

        return 0;
      },
    },
  );
}

export function useDataMaidEntityActions() {
  const workspaceId = useWorkspaceId();
  const currentUser = useCurrentUser();
  const { cache, mutate } = useSWRConfig();

  const autofillEntityFields = useCallback(
    async (entityId: string) => {
      await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/datamaid/field-autofill/${entityId}`,
        {
          method: 'POST',
          headers: {
            'X-Finsight-Token': currentUser?.token || '',
          },
        },
      );

      Array.from(cache.keys())
        .filter(
          (x) =>
            x.includes(
              `/workspace/${workspaceId}/datamaid/field-autofill/${entityId}/status`,
            ) ||
            x.includes(
              `/workspace/${workspaceId}/datamaid/history/field-autofill`,
            ),
        )
        .forEach((key) => mutate(key));
    },
    [workspaceId, currentUser, cache, mutate],
  );

  const batchManualfillEntityFields = useCallback(
    async (
      items: {
        entityId: string;
        fields: string[];
        instructions: string;
        websearch: boolean;
      }[],
    ) => {
      if (items.length === 0) return;

      await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/datamaid/field-manualfill-batch`,
        {
          method: 'POST',
          headers: {
            'X-Finsight-Token': currentUser?.token || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        },
      );

      const entityIds = items.map((item) => item.entityId);

      Array.from(cache.keys())
        .filter(
          (x) =>
            entityIds.some((entityId) =>
              x.includes(
                `/workspace/${workspaceId}/datamaid/field-autofill/${entityId}/status`,
              ),
            ) ||
            x.includes(
              `/workspace/${workspaceId}/datamaid/history/field-autofill`,
            ),
        )
        .forEach((key) => mutate(key));
    },
    [workspaceId, currentUser, cache, mutate],
  );

  const manualfillEntityFields = useCallback(
    async (
      entityId: string,
      fields: string[],
      instructions: string,
      websearch: boolean,
    ) => {
      await batchManualfillEntityFields([
        { entityId, fields, instructions, websearch },
      ]);
    },
    [batchManualfillEntityFields],
  );

  return {
    autofillEntityFields,
    manualfillEntityFields,
    batchManualfillEntityFields,
  };
}
